import React, { useState, useEffect } from 'react';
import {
  Upload,
  RefreshCw,
  Download,
  Trash2,
  FileText,
  Play,
  CheckCircle,
  XCircle,
  Clock,
  Cloud,
  BookOpen,
  BarChart3,
  Loader2,
  File as FileIcon,
  Eye,
  MonitorPlay,
} from 'lucide-react';
import { PageHeader } from '../../components/PageHeader';
import { contentAssemblyService } from '../../services/content-assembly.service';
import SharePointBrowser from '../../components/SharePointBrowser';
import sharePointSyncService from '../../services/sharepoint-sync.service';
import { confirmDelete, showDeleteSuccess, showDeleteError } from '../../utils/deleteConfirmation';
import { API_BASE_URL } from '../../config/api.config';

interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      className={value === index ? 'block' : 'hidden'}
    >
      {value === index && <div className="p-6">{children}</div>}
    </div>
  );
}

const ContentAssemblyDashboard: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [trilhas, setTrilhas] = useState<any[]>([]);
  const [apostilas, setApostilas] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // DOCX conversion is a background job (can take 10-20+ min on large apostilas),
  // tracked per apostila+part so multiple parts can convert independently
  const [docxConversions, setDocxConversions] = useState<Record<string, { status: string; progress?: number; error?: string }>>({});

  // Pagination state
  const [trilhasPage, setTrilhasPage] = useState(1);
  const [trilhasLimit] = useState(9);
  const [trilhasPagination, setTrilhasPagination] = useState({ total: 0, totalPages: 0 });

  const [apostilasPage, setApostilasPage] = useState(1);
  const [apostilasLimit] = useState(9);
  const [apostilasPagination, setApostilasPagination] = useState({ total: 0, totalPages: 0 });

  // Upload dialog
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Assembly dialog
  const [assemblyDialogOpen, setAssemblyDialogOpen] = useState(false);
  const [selectedTrilha, setSelectedTrilha] = useState<any>(null);
  const [assemblyConfig, setAssemblyConfig] = useState({
    name: '',
    pageLimit: 460,
    addPageNumbers: true,
    // NBA-RQ-3: mutually exclusive summary options - per-OA resumos ('oa'),
    // the track-level Full Summary ('full'), or none
    summaryType: 'none' as 'oa' | 'full' | 'none',
    sharePointDriveId: '',
    sharePointDriveName: '',
    sharePointFolderId: '',
  });

  // NBA-RQ-3: Add Slides dialog - user manually picks one deck per OA
  const [slidesDialogOpen, setSlidesDialogOpen] = useState(false);
  const [slidesApostila, setSlidesApostila] = useState<any>(null);
  const [slidesTrilhaOAs, setSlidesTrilhaOAs] = useState<{ oaCode: string; title?: string }[]>([]);
  // Client 2026-10: ONE ordered deck list - the on-screen order IS the final
  // order in the document. oeCode optional (extra decks like exam tips allowed).
  const [deckList, setDeckList] = useState<{ fileId: string; driveId: string; fileName: string; oeCode?: string }[]>([]);
  const [slidesPerPage, setSlidesPerPage] = useState<1 | 2 | 4>(2);
  const [deckBrowserForOA, setDeckBrowserForOA] = useState<string | null>(null);
  const [slidesLoadingOAs, setSlidesLoadingOAs] = useState(false);

  // Client 2026-09-25: single-step bulk selection - open the slides folder ONCE,
  // pick all decks; they're auto-assigned to OAs by the filename's number prefix
  // (e.g. "077_Slides_x.pptx" -> OA 077). Unmatched decks STAY in the list as
  // extra decks (client 2026-10) and can be OA-assigned or left as-is.
  const [bulkDeckBrowserOpen, setBulkDeckBrowserOpen] = useState(false);

  // Add Slides merge is a background job, tracked per apostila (like DOCX)
  const [slidesJobs, setSlidesJobs] = useState<Record<string, {
    status: string;
    progress?: number;
    currentStep?: string;
    error?: string;
    report?: { addedOEs: string[]; skippedOEs: { oeCode: string; fileName?: string; reason: string }[] };
  }>>({});

  // SharePoint browser
  const [sharePointBrowserOpen, setSharePointBrowserOpen] = useState(false);
  const [sharePointBrowserOpenForUpload, setSharePointBrowserOpenForUpload] = useState(false);
  const [selectedSharePointFile, setSelectedSharePointFile] = useState<{ driveId: string; fileId: string; fileName: string; filePath: string } | null>(null);

  // Multi-file selection for assembly
  const [selectedOEFiles, setSelectedOEFiles] = useState<any[]>([]);

  useEffect(() => {
    loadData();
  }, [currentTab, trilhasPage, apostilasPage]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (currentTab === 0) {
        const response = await contentAssemblyService.getTrilhas(trilhasPage, trilhasLimit);
        setTrilhas(response.data || []);
        if (response.pagination) {
          setTrilhasPagination({
            total: response.pagination.total,
            totalPages: response.pagination.totalPages,
          });
        }
      } else if (currentTab === 1) {
        const response = await contentAssemblyService.getApostilas({}, apostilasPage, apostilasLimit);
        setApostilas(response.data || []);
        if (response.pagination) {
          setApostilasPagination({
            total: response.pagination.total,
            totalPages: response.pagination.totalPages,
          });
        }
      } else if (currentTab === 2) {
        const response = await contentAssemblyService.getStats();
        setStats(response.data || {});
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to load data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setSelectedSharePointFile(null);
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.name.endsWith('.docx')) {
        setSelectedFile(file);
        setSelectedSharePointFile(null);
      } else {
        setError('Only .docx files are allowed');
      }
    }
  };

  const handleUploadTrilha = async () => {
    if (!selectedFile && !selectedSharePointFile) return;

    try {
      setLoading(true);
      setError(null);

      let fileToUpload: File;

      if (selectedSharePointFile) {
        const blob = await sharePointSyncService.downloadFile(
          selectedSharePointFile.driveId,
          selectedSharePointFile.fileId
        );

        fileToUpload = new File([blob], selectedSharePointFile.fileName, {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        });
      } else if (selectedFile) {
        fileToUpload = selectedFile;
      } else {
        return;
      }

      const response = await contentAssemblyService.uploadTrilha(fileToUpload);
      setSuccess(`✓ Trilha uploaded successfully! ${response.data.totalOAs} OAs found in ${response.data.modules?.length || response.data.sections.length} sections/modules.`);
      setUploadDialogOpen(false);
      setSelectedFile(null);
      setSelectedSharePointFile(null);
      setIsDragging(false);
      loadData();
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to upload trilha';
      setError(errorMessage);
      console.error('Upload error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTrilha = async (id: string) => {
    const confirmed = await confirmDelete({
      title: 'Delete Trilha?',
      text: 'This action cannot be undone. Are you sure you want to delete this trilha?',
    });

    if (!confirmed) return;

    try {
      await contentAssemblyService.deleteTrilha(id);
      showDeleteSuccess('Trilha deleted successfully');

      if (trilhas.length === 1 && trilhasPage > 1) {
        setTrilhasPage(trilhasPage - 1);
      } else {
        loadData();
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to delete trilha';
      showDeleteError(errorMessage);
    }
  };

  const handleStartAssembly = (trilha: any) => {
    setSelectedTrilha(trilha);

    const name = trilha.trilhaName || trilha.name || 'Unknown';
    const yearOrVersion = trilha.year?.toString() || trilha.version || '';
    const versionAlreadyInName = yearOrVersion && name.includes(yearOrVersion);
    const apostilaName = versionAlreadyInName
      ? name
      : yearOrVersion ? `${name} - ${yearOrVersion}` : name;

    setAssemblyConfig({
      name: apostilaName,
      pageLimit: 460,
      addPageNumbers: true,
      summaryType: 'none',
      sharePointDriveId: '',
      sharePointDriveName: '',
      sharePointFolderId: '',
    });
    setAssemblyDialogOpen(true);
  };


  const handleSharePointSelect = (driveId: string, driveName: string, folderPath?: string, folderId?: string, _fileName?: string, _fileId?: string, selectedFiles?: any[]) => {
    const folderName = folderPath ? `${driveName}/${folderPath}` : driveName;

    if (selectedFiles && selectedFiles.length > 0) {
      setSelectedOEFiles(selectedFiles);
    } else {
      setSelectedOEFiles([]);
    }

    setAssemblyConfig({
      ...assemblyConfig,
      sharePointDriveId: driveId,
      sharePointDriveName: folderName,
      sharePointFolderId: folderId || '',
    });

    setSharePointBrowserOpen(false);
  };

  const handleSharePointSelectForUpload = (driveId: string, driveName: string, folderPath?: string, _folderId?: string, fileName?: string, fileId?: string) => {
    if (fileName && fileId) {
      setSelectedSharePointFile({
        driveId,
        fileId,
        fileName,
        filePath: folderPath ? `${driveName}/${folderPath}/${fileName}` : `${driveName}/${fileName}`,
      });
      setSelectedFile(null);
      setSharePointBrowserOpenForUpload(false);
    }
  };

  const handleCreateApostila = async () => {
    if (!selectedTrilha) return;

    if (!assemblyConfig.sharePointDriveId) {
      setError('Please select a SharePoint library');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await contentAssemblyService.createApostila({
        trilhaId: selectedTrilha._id,
        name: assemblyConfig.name,
        pageLimit: assemblyConfig.pageLimit,
        addPageNumbers: assemblyConfig.addPageNumbers,
        summaryType: assemblyConfig.summaryType,
        includeResumo: assemblyConfig.summaryType === 'oa', // legacy mirror
        sharePointDriveId: assemblyConfig.sharePointDriveId,
        sharePointFolderId: assemblyConfig.sharePointFolderId,
      });

      setSuccess('✓ Assembly started! Check the Apostilas tab to monitor progress.');
      setAssemblyDialogOpen(false);
      setSelectedTrilha(null);

      setCurrentTab(1);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to start assembly';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };


  const handleRetryWithCached = async (apostila: any) => {
    try {
      setLoading(true);
      setError(null);

      await contentAssemblyService.createApostila({
        trilhaId: apostila.trilhaId,
        name: `${apostila.name} (Retry)`,
        pageLimit: apostila.pageLimit || 460,
        addPageNumbers: apostila.config?.addPageNumbers !== false,
        summaryType: apostila.config?.summaryType
          ?? (apostila.config?.includeResumo === true ? 'oa' : 'none'),
        sharePointDriveId: apostila.config?.sharePointDriveId,
        sharePointFolderId: apostila.config?.sharePointFolderId,
      });

      setSuccess('✓ Retry started with cached OEs! Missing OEs will be skipped.');

      setTimeout(() => loadData(), 1000);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to retry assembly';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadApostila = async (
    apostila: any,
    partNumber?: number,
    format: 'pdf' | 'docx' = 'pdf',
    options: { rethrow?: boolean } = {}
  ) => {
    try {
      setLoading(true);

      const blob = await contentAssemblyService.downloadApostila(apostila._id, partNumber, format);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = partNumber
        ? `${apostila.name} - Part ${partNumber}.${format}`
        : `${apostila.name}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setSuccess(`✓ Download started: Part ${partNumber}`);
    } catch (error: any) {
      if (options.rethrow) throw error;
      const defaultMessage = format === 'docx'
        ? 'DOCX conversion failed, please try again or use the PDF download'
        : 'Failed to download apostila';
      const errorMessage = error.response?.data?.error || error.message || defaultMessage;
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // DOCX conversion runs as a background job (10-20+ min on large apostilas). Clicking
  // starts the job (or just downloads immediately if already cached) then polls status -
  // it never auto-downloads on completion, since the wait is long enough that the user
  // may have navigated away or refreshed; instead the button flips to a "ready, click to
  // download" state that survives a page reload (see the hydration effect below)
  const handleDownloadDocx = async (apostila: any, partNumber: number) => {
    const key = `${apostila._id}_${partNumber}`;

    const startConversionFlow = async () => {
      try {
        setDocxConversions(prev => ({ ...prev, [key]: { status: 'processing' } }));
        const result = await contentAssemblyService.startDocxConversion(apostila._id, partNumber);

        if (result.status === 'completed') {
          setDocxConversions(prev => ({ ...prev, [key]: { status: 'completed' } }));
          await handleDownloadApostila(apostila, partNumber, 'docx');
          return;
        }

        pollDocxStatus(apostila, partNumber, key);
      } catch (error: any) {
        const errorMessage = error.response?.data?.error || error.message || 'Failed to start DOCX conversion';
        setDocxConversions(prev => ({ ...prev, [key]: { status: 'failed', error: errorMessage } }));
        setError(errorMessage);
      }
    };

    if (docxConversions[key]?.status === 'completed') {
      try {
        await handleDownloadApostila(apostila, partNumber, 'docx', { rethrow: true });
      } catch (error: any) {
        // Client thought this was cached (e.g. stale state from an earlier session) but the
        // server disagrees - self-heal by re-running the normal start/poll flow instead of
        // leaving the button stuck on a "ready" state that will keep failing
        if (error.response?.data?.code === 'DOCX_NOT_READY') {
          await startConversionFlow();
        } else {
          const errorMessage = error.response?.data?.error || error.message || 'Failed to download apostila';
          setError(errorMessage);
        }
      }
      return;
    }

    await startConversionFlow();
  };

  const pollDocxStatus = (apostila: any, partNumber: number, key: string) => {
    const POLL_INTERVAL_MS = 10000;

    const poll = async () => {
      try {
        const statusResult = await contentAssemblyService.getDocxStatus(apostila._id, partNumber);
        setDocxConversions(prev => ({ ...prev, [key]: statusResult }));

        if (statusResult.status === 'processing' || statusResult.status === 'pending') {
          setTimeout(poll, POLL_INTERVAL_MS);
        }
        // 'completed'/'failed' stop here - the button already reflects it via docxConversions
      } catch (error: any) {
        // Transient network hiccup - keep polling rather than giving up
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    setTimeout(poll, POLL_INTERVAL_MS);
  };

  // A DOCX conversion can outlive the page (10-20+ min): if the user refreshes or the
  // apostila list reloads mid-conversion, resume tracking from the server's own record
  // instead of losing the in-progress/ready state that only lived in local component state
  useEffect(() => {
    const updates: Record<string, { status: string; progress?: number; error?: string }> = {};

    apostilas.forEach((apostila: any) => {
      (apostila.parts || []).forEach((part: any) => {
        const key = `${apostila._id}_${part.partNumber}`;
        if (docxConversions[key]) return; // already tracked client-side

        const idx = part.partNumber - 1;
        const hasCached = apostila.docxOutputPaths?.[idx] || apostila.docxS3Keys?.[idx];
        const serverStatus = apostila.docxStatuses?.[idx]?.status;

        if (hasCached) {
          updates[key] = { status: 'completed' };
        } else if (serverStatus === 'processing') {
          updates[key] = { status: 'processing', progress: apostila.docxStatuses[idx].progress };
          pollDocxStatus(apostila, part.partNumber, key);
        } else if (serverStatus === 'failed') {
          updates[key] = { status: 'failed', error: apostila.docxStatuses[idx].error };
        }
      });
    });

    if (Object.keys(updates).length > 0) {
      setDocxConversions(prev => ({ ...prev, ...updates }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apostilas]);

  // ═══════════════════════════════════════════════════════════════
  // NBA-RQ-3: Add Slides merge (content → slides → summary)
  // ═══════════════════════════════════════════════════════════════

  const handleOpenSlidesDialog = async (apostila: any) => {
    setSlidesApostila(apostila);
    setDeckList([]);
    setSlidesPerPage(2);
    setSlidesTrilhaOAs([]);
    setSlidesDialogOpen(true);

    // The OA list comes from the apostila's trilha - the user picks a deck
    // for each OA they want slides for (subset allowed)
    setSlidesLoadingOAs(true);
    try {
      const response = await contentAssemblyService.getTrilha(apostila.trilhaId);
      const trilha = response.data;
      const oas: { oaCode: string; title?: string }[] = [];
      const seen = new Set<string>();
      (trilha?.sections || []).forEach((section: any) => {
        (section.oas || []).forEach((oa: any) => {
          if (!seen.has(oa.oaCode)) {
            seen.add(oa.oaCode);
            oas.push({ oaCode: oa.oaCode, title: oa.title });
          }
        });
      });
      setSlidesTrilhaOAs(oas);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to load trilha OAs';
      setError(errorMessage);
    } finally {
      setSlidesLoadingOAs(false);
    }
  };

  const handleDeckSelect = (
    driveId: string,
    _driveName: string,
    _folderPath?: string,
    _folderId?: string,
    _fileName?: string,
    _fileId?: string,
    selectedFiles?: any[]
  ) => {
    // Per-OA fallback picker: append the chosen decks (tagged with this OA)
    // at the END of the ordered list (dedup by fileId)
    if (deckBrowserForOA && selectedFiles && selectedFiles.length > 0) {
      const oa = deckBrowserForOA;
      setDeckList(prev => {
        const seen = new Set(prev.map(d => d.fileId));
        const additions = selectedFiles
          .filter(f => !seen.has(f.id))
          .map(f => ({ fileId: f.id, driveId, fileName: f.name, oeCode: oa }));
        return [...prev, ...additions];
      });
    }
    setDeckBrowserForOA(null);
  };

  // Bulk selection (client 2026-09-25/10): all picked decks are appended to the
  // ordered list IN PICK ORDER; each is auto-tagged with its OA via the
  // filename's number prefix. Unmatched decks stay in the list as extra decks.
  const handleBulkDeckSelect = (
    driveId: string,
    _driveName: string,
    _folderPath?: string,
    _folderId?: string,
    _fileName?: string,
    _fileId?: string,
    selectedFiles?: any[]
  ) => {
    if (selectedFiles && selectedFiles.length > 0) {
      const validOAs = new Set(slidesTrilhaOAs.map(oa => oa.oaCode));
      let matchedCount = 0;
      let extraCount = 0;

      setDeckList(prev => {
        const seen = new Set(prev.map(d => d.fileId));
        const additions = selectedFiles
          .filter(f => !seen.has(f.id))
          .map(f => {
            // Leading number followed by any non-digit (\b fails on "077_" since _ is a word char)
            const numMatch = (f.name || '').match(/^\s*(\d{1,3})(?=\D|$)/);
            const oaCode = numMatch ? numMatch[1].padStart(3, '0') : null;
            if (oaCode && validOAs.has(oaCode)) {
              matchedCount++;
              return { fileId: f.id, driveId, fileName: f.name, oeCode: oaCode };
            }
            extraCount++;
            return { fileId: f.id, driveId, fileName: f.name };
          });
        return [...prev, ...additions];
      });

      setSuccess(`✓ Decks added in selection order — ${matchedCount} matched to OAs by filename${extraCount > 0 ? `, ${extraCount} added as extra deck(s) (no OA)` : ''}`);
    }
    setBulkDeckBrowserOpen(false);
  };

  const setDeckOA = (fileId: string, oaCode: string | undefined) => {
    setDeckList(prev => prev.map(d => d.fileId === fileId ? { ...d, oeCode: oaCode } : d));
  };

  const moveDeck = (index: number, direction: -1 | 1) => {
    setDeckList(prev => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const handleStartSlidesMerge = async () => {
    if (!slidesApostila) return;

    // The list order IS the final order in the document (client 2026-10)
    const slides = deckList.map(d => ({
      oeCode: d.oeCode,
      fileId: d.fileId,
      driveId: d.driveId,
      fileName: d.fileName,
    }));

    if (slides.length === 0) {
      setError('Select at least one slide deck');
      return;
    }

    const apostilaId = slidesApostila._id;
    try {
      setSlidesJobs(prev => ({ ...prev, [apostilaId]: { status: 'processing', progress: 0 } }));
      await contentAssemblyService.addSlides(apostilaId, { slides, slidesPerPage });
      setSlidesDialogOpen(false);
      setSlidesApostila(null);
      setSuccess(`✓ Slides merge started for ${slides.length} OA(s)! Progress is shown on the apostila card.`);
      pollSlidesStatus(apostilaId);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to start slides merge';
      setSlidesJobs(prev => ({ ...prev, [apostilaId]: { status: 'failed', error: errorMessage } }));
      setError(errorMessage);
    }
  };

  const pollSlidesStatus = (apostilaId: string) => {
    const POLL_INTERVAL_MS = 5000;

    const poll = async () => {
      try {
        const statusResult = await contentAssemblyService.getSlidesStatus(apostilaId);
        setSlidesJobs(prev => ({ ...prev, [apostilaId]: statusResult }));

        if (statusResult.status === 'processing' || statusResult.status === 'pending') {
          setTimeout(poll, POLL_INTERVAL_MS);
        }
      } catch (error: any) {
        // Transient network hiccup - keep polling rather than giving up
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    setTimeout(poll, POLL_INTERVAL_MS);
  };

  const handleDownloadMerged = async (apostila: any, partNumber?: number) => {
    try {
      setLoading(true);
      const blob = await contentAssemblyService.downloadMergedApostila(apostila._id, partNumber);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = partNumber
        ? `${apostila.name} - Part ${partNumber} (com slides).pdf`
        : `${apostila.name} (com slides).pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setSuccess('✓ Download started (com slides)');
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to download merged apostila';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Resume Add Slides tracking after a refresh from the server's own record
  // (same rationale as the DOCX hydration effect above)
  useEffect(() => {
    const updates: Record<string, any> = {};

    apostilas.forEach((apostila: any) => {
      if (slidesJobs[apostila._id]) return; // already tracked client-side
      const serverStatus = apostila.slidesStatus?.status;
      if (!serverStatus) return;

      updates[apostila._id] = {
        status: serverStatus,
        progress: apostila.slidesStatus.progress,
        currentStep: apostila.slidesStatus.currentStep,
        error: apostila.slidesStatus.error,
        report: apostila.slidesReport || undefined,
      };
      if (serverStatus === 'processing') {
        pollSlidesStatus(apostila._id);
      }
    });

    if (Object.keys(updates).length > 0) {
      setSlidesJobs(prev => ({ ...prev, ...updates }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apostilas]);

  const handlePreviewApostila = (apostilaId: string, partNumber?: number) => {
    // Build the preview URL using the new VIEW endpoint (BA-RQ-29)
    const partQuery = partNumber ? `?part=${partNumber}` : '';
    const viewUrl = `${API_BASE_URL}/content-assembly/apostilas/${apostilaId}/view${partQuery}`;

    // Open in new tab for inline viewing
    window.open(viewUrl, '_blank');
  };

  const handleDeleteApostila = async (id: string) => {
    const confirmed = await confirmDelete({
      title: 'Delete Apostila?',
      text: 'This action cannot be undone. Are you sure you want to delete this apostila?',
    });

    if (!confirmed) return;

    try {
      await contentAssemblyService.deleteApostila(id);
      showDeleteSuccess('Apostila deleted successfully');

      if (apostilas.length === 1 && apostilasPage > 1) {
        setApostilasPage(apostilasPage - 1);
      } else {
        loadData();
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to delete apostila';
      showDeleteError(errorMessage);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; className: string; icon: React.ReactNode }> = {
      pending: { label: 'Pending', className: 'bg-gray-100 text-gray-700', icon: <Clock className="w-4 h-4" /> },
      processing: { label: 'Processing', className: 'bg-blue-100 text-blue-700', icon: <Loader2 className="w-4 h-4 animate-spin" /> },
      completed: { label: 'Completed', className: 'bg-green-100 text-green-700', icon: <CheckCircle className="w-4 h-4" /> },
      failed: { label: 'Failed', className: 'bg-red-100 text-red-700', icon: <XCircle className="w-4 h-4" /> },
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold ${config.className}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-start justify-between">
        <PageHeader
          title="Book Assembly System"
          description="Automatically generate apostilas with integrated summaries from Trilha documents"
          breadcrumbs={[{ label: 'Content Assembly' }]}
        />
        <button
          onClick={() => setUploadDialogOpen(true)}
          className="mt-10 flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors shadow-md"
        >
          <Upload className="w-5 h-5" />
          Upload Trilha
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
          <span className="text-red-800">{error}</span>
          <button
            onClick={() => setError(null)}
            className="text-red-600 hover:text-red-800 font-bold text-xl"
          >
            ×
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between">
          <span className="text-green-800">{success}</span>
          <button
            onClick={() => setSuccess(null)}
            className="text-green-600 hover:text-green-800 font-bold text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex">
            <button
              onClick={() => setCurrentTab(0)}
              className={`flex items-center gap-2 px-6 py-4 font-semibold transition-colors border-b-2 ${
                currentTab === 0
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <FileIcon className="w-5 h-5" />
              Trilhas
            </button>
            <button
              onClick={() => setCurrentTab(1)}
              className={`flex items-center gap-2 px-6 py-4 font-semibold transition-colors border-b-2 ${
                currentTab === 1
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <BookOpen className="w-5 h-5" />
              Apostilas
            </button>
            <button
              onClick={() => setCurrentTab(2)}
              className={`flex items-center gap-2 px-6 py-4 font-semibold transition-colors border-b-2 ${
                currentTab === 2
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <BarChart3 className="w-5 h-5" />
              Statistics
            </button>
          </nav>
        </div>

        {/* Trilhas Tab */}
        <TabPanel value={currentTab} index={0}>
          <div className="mb-4 flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900">Uploaded Trilha Documents</h2>
            <button
              onClick={loadData}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 font-medium transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-10 h-10 text-blue-600 animate-spin mb-3" />
              <p className="text-gray-600">Loading...</p>
            </div>
          ) : trilhas.length === 0 ? (
            <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No trilhas found</h3>
              <p className="text-gray-500 mb-6">Upload a .docx document to get started</p>
              <button
                onClick={() => setUploadDialogOpen(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
              >
                <Upload className="w-5 h-5" />
                Upload Trilha
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {trilhas.map((trilha) => (
                <div
                  key={trilha._id}
                  className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-all overflow-hidden"
                >
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">
                      {(() => {
                        const name = trilha.trilhaName || trilha.name || 'Unknown';
                        const yearOrVersion = trilha.year?.toString() || trilha.version || '';
                        return yearOrVersion && name.includes(yearOrVersion) ? name : `${name}${yearOrVersion ? ' - ' + yearOrVersion : ''}`;
                      })()}
                    </h3>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">OEs:</span>
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                          {trilha.totalOAs || trilha.totalOEs || 0}
                        </span>
                      </div>

                      <p className="text-xs text-gray-500">
                        Created: {new Date(trilha.createdAt).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true
                        })}
                      </p>

                      {trilha.createdBy && (
                        <p className="text-xs text-gray-500">
                          Created by: {trilha.createdBy}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="border-t border-gray-200 p-4 flex gap-2">
                    <button
                      onClick={() => handleStartAssembly(trilha)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                    >
                      <BookOpen className="w-4 h-4" />
                      Create Apostila (includes summaries)
                    </button>
                    <button
                      onClick={() => handleDeleteTrilha(trilha._id)}
                      className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {!loading && trilhas.length > 0 && trilhasPagination.totalPages > 1 && (
            <div className="flex justify-center mt-6 gap-2">
              {Array.from({ length: trilhasPagination.totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => setTrilhasPage(page)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    trilhasPage === page
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
          )}
        </TabPanel>

        {/* Apostilas Tab */}
        <TabPanel value={currentTab} index={1}>
          <div className="mb-4 flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900">Assembly Jobs</h2>
            <button
              onClick={loadData}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 font-medium transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-10 h-10 text-blue-600 animate-spin mb-3" />
              <p className="text-gray-600">Loading...</p>
            </div>
          ) : apostilas.length === 0 ? (
            <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
              <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No apostilas in progress</h3>
              <p className="text-gray-500">Start an assembly from the Trilhas tab</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {apostilas.map((apostila) => (
                <div
                  key={apostila._id}
                  className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden"
                >
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">
                      {apostila.name}
                    </h3>

                    <div className="space-y-2 mb-4">
                      <p className="text-xs text-gray-500">
                        Created: {new Date(apostila.createdAt).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true
                        })}
                      </p>

                      {apostila.createdBy && (
                        <p className="text-xs text-gray-500">
                          Created by: {apostila.createdBy}
                        </p>
                      )}

                      {apostila.completedAt && (
                        <p className="text-xs text-gray-500">
                          Completed: {new Date(apostila.completedAt).toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </p>
                      )}
                    </div>

                    <div className="mb-4">
                      {getStatusBadge(apostila.status.status)}
                    </div>

                    {apostila.status.status === 'processing' && (
                      <div className="mt-4">
                        <p className="text-xs text-gray-600 mb-2">{apostila.status.currentStep}</p>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all"
                            style={{ width: `${apostila.status.progress}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-600 mt-1">{apostila.status.progress}% Complete</p>
                      </div>
                    )}

                    {apostila.status.status === 'completed' && (
                      <div className="mt-4">
                        <p className="text-sm text-gray-600 mb-2">
                          {apostila.parts.length} part(s) • {apostila.totalPages} pages
                        </p>

                        {apostila.skippedOEs && apostila.skippedOEs.length > 0 && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                            <p className="text-sm text-yellow-800">
                              ⚠️ {apostila.skippedOEs.length} OE(s) not found: {apostila.skippedOEs.join(', ')}
                            </p>
                          </div>
                        )}

                        <div className="space-y-2">
                          {apostila.parts.map((part: any) => {
                            const docxState = docxConversions[`${apostila._id}_${part.partNumber}`];
                            const isConverting = docxState?.status === 'processing' || docxState?.status === 'pending';
                            const docxFailed = docxState?.status === 'failed';
                            const docxReady = docxState?.status === 'completed';

                            return (
                              <div key={part.partNumber}>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handlePreviewApostila(apostila._id, part.partNumber)}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-blue-300 bg-blue-50 rounded-lg text-blue-700 hover:bg-blue-100 font-medium transition-colors"
                                    title="Preview PDF in browser"
                                  >
                                    <Eye className="w-4 h-4" />
                                    Preview
                                  </button>
                                  <button
                                    onClick={() => handleDownloadApostila(apostila, part.partNumber)}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                                    title="Download PDF to computer"
                                  >
                                    <Download className="w-4 h-4" />
                                    Download
                                  </button>
                                  <button
                                    onClick={() => handleDownloadDocx(apostila, part.partNumber)}
                                    disabled={isConverting}
                                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 border rounded-lg font-medium transition-colors ${
                                      docxReady
                                        ? 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100'
                                        : docxFailed
                                        ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100'
                                        : isConverting
                                        ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                    }`}
                                    title={
                                      docxReady
                                        ? 'DOCX ready - click to download'
                                        : isConverting
                                        ? 'Converting to DOCX - this can take 10-20+ minutes for large apostilas'
                                        : 'Download editable DOCX (converted from PDF, best-effort formatting)'
                                    }
                                  >
                                    {isConverting ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : docxReady ? (
                                      <CheckCircle className="w-4 h-4" />
                                    ) : (
                                      <FileText className="w-4 h-4" />
                                    )}
                                    {isConverting ? 'Converting…' : docxFailed ? 'Retry DOCX' : docxReady ? 'DOCX Ready' : 'DOCX'}
                                  </button>
                                </div>
                                {isConverting && (
                                  <div className="mt-1.5">
                                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                                      <div
                                        className="bg-blue-600 h-1.5 rounded-full transition-all"
                                        style={{ width: `${docxState?.progress ?? 5}%` }}
                                      />
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                      Converting to DOCX… ~{docxState?.progress ?? 5}% (estimated - can take 10-20+ min for large apostilas)
                                    </p>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          <p className="text-xs text-gray-500 mt-2">
                            Part {apostila.parts.length === 1 ? apostila.parts[0].partNumber : `1-${apostila.parts.length}`} • {apostila.totalPages} total pages
                          </p>
                        </div>

                        {/* NBA-RQ-3: warning when the Full Summary PDF was not found */}
                        {apostila.summaryWarning && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-3">
                            <p className="text-sm text-yellow-800">⚠️ {apostila.summaryWarning}</p>
                          </div>
                        )}

                        {/* NBA-RQ-3: Add Slides (optional) - final order: content → slides → summary */}
                        {(() => {
                          const slidesJob = slidesJobs[apostila._id];
                          const isMerging = slidesJob?.status === 'processing' || slidesJob?.status === 'pending';
                          const mergeDone = slidesJob?.status === 'completed';
                          const mergeFailed = slidesJob?.status === 'failed';

                          return (
                            <div className="mt-3 border-t border-gray-100 pt-3">
                              {isMerging ? (
                                <div>
                                  <p className="text-xs text-gray-600 mb-1">
                                    {slidesJob?.currentStep || 'Generating slides…'}
                                  </p>
                                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                                    <div
                                      className="bg-purple-600 h-1.5 rounded-full transition-all"
                                      style={{ width: `${slidesJob?.progress ?? 5}%` }}
                                    />
                                  </div>
                                </div>
                              ) : mergeDone ? (
                                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 space-y-2">
                                  <p className="text-sm font-semibold text-purple-800">
                                    ✓ Slides added ({slidesJob?.report?.addedOEs?.length || 0} OA(s))
                                  </p>
                                  {slidesJob?.report?.skippedOEs && slidesJob.report.skippedOEs.length > 0 && (
                                    <p className="text-xs text-yellow-800 bg-yellow-50 border border-yellow-200 rounded p-2">
                                      ⚠️ {slidesJob.report.skippedOEs.length} OA(s) skipped:{' '}
                                      {slidesJob.report.skippedOEs.map(s => `${s.oeCode} (${s.reason})`).join('; ')}
                                    </p>
                                  )}
                                  <div className="flex flex-wrap gap-2">
                                    {apostila.parts.map((part: any) => (
                                      <button
                                        key={part.partNumber}
                                        onClick={() => handleDownloadMerged(apostila, part.partNumber)}
                                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-purple-300 bg-white rounded-lg text-purple-700 hover:bg-purple-100 font-medium transition-colors text-sm"
                                        title="Download the single final PDF (content → slides → summary)"
                                      >
                                        <Download className="w-4 h-4" />
                                        {apostila.parts.length > 1 ? `Part ${part.partNumber} (com slides)` : 'Download (com slides)'}
                                      </button>
                                    ))}
                                    <button
                                      onClick={() => handleOpenSlidesDialog(apostila)}
                                      className="px-3 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 font-medium transition-colors text-sm"
                                      title="Redo the slides merge with a different selection"
                                    >
                                      Redo
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  {mergeFailed && (
                                    <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 mb-2">
                                      Slides merge failed: {slidesJob?.error}
                                    </p>
                                  )}
                                  <button
                                    onClick={() => handleOpenSlidesDialog(apostila)}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-purple-300 bg-purple-50 rounded-lg text-purple-700 hover:bg-purple-100 font-medium transition-colors"
                                    title="Append PPTX Converter slides to this apostila (optional)"
                                  >
                                    <MonitorPlay className="w-4 h-4" />
                                    {mergeFailed ? 'Retry Add Slides' : 'Add Slides'}
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    )}

                    {apostila.status.status === 'failed' && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-4">
                        <p className="text-sm text-red-800">{apostila.status.error}</p>
                      </div>
                    )}
                  </div>

                  {apostila.status.status === 'failed' && (
                    <div className="border-t border-gray-200 p-4 flex justify-between items-center">
                      <button
                        onClick={() => handleRetryWithCached(apostila)}
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                      >
                        <RefreshCw className="w-4 h-4" />
                        Retry with Cached OEs
                      </button>
                      <button
                        onClick={() => handleDeleteApostila(apostila._id)}
                        className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  )}

                  {apostila.status.status === 'completed' && (
                    <div className="border-t border-gray-200 p-4 flex justify-end">
                      <button
                        onClick={() => handleDeleteApostila(apostila._id)}
                        className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {!loading && apostilas.length > 0 && apostilasPagination.totalPages > 1 && (
            <div className="flex justify-center mt-6 gap-2">
              {Array.from({ length: apostilasPagination.totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => setApostilasPage(page)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    apostilasPage === page
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
          )}
        </TabPanel>

        {/* Statistics Tab */}
        <TabPanel value={currentTab} index={2}>
          <h2 className="text-xl font-bold text-gray-900 mb-6">System Statistics</h2>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-10 h-10 text-blue-600 animate-spin mb-3" />
              <p className="text-gray-600">Loading...</p>
            </div>
          ) : stats ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-6 text-center">
                <div className="text-4xl font-bold text-purple-600 mb-2">{stats.totalTrilhas}</div>
                <div className="text-sm font-semibold text-gray-600">Total Trilhas</div>
              </div>

              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">{stats.totalApostilas}</div>
                <div className="text-sm font-semibold text-gray-600">Total Apostilas</div>
              </div>

              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6 text-center">
                <div className="text-4xl font-bold text-green-600 mb-2">{stats.completedApostilas}</div>
                <div className="text-sm font-semibold text-gray-600">Completed</div>
              </div>

              <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-6 text-center">
                <div className="text-4xl font-bold text-yellow-600 mb-2">{stats.processingApostilas}</div>
                <div className="text-sm font-semibold text-gray-600">Processing</div>
              </div>
            </div>
          ) : null}
        </TabPanel>
      </div>

      {/* Upload Dialog */}
      {uploadDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Upload Trilha Document</h2>
            </div>

            <div className="p-6 space-y-6">
              {/* Local File Upload */}
              <div>
                <h3 className="text-lg font-semibold text-blue-600 mb-3">Upload from Computer</h3>
                <input
                  accept=".docx"
                  style={{ display: 'none' }}
                  id="trilha-file-upload"
                  type="file"
                  onChange={handleFileSelect}
                />
                <label htmlFor="trilha-file-upload">
                  <div
                    className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${
                      isDragging
                        ? 'border-blue-500 bg-blue-50 scale-105'
                        : selectedFile
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-300 bg-gray-50 hover:border-blue-500 hover:bg-blue-50'
                    }`}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  >
                    <FileText className={`w-16 h-16 mx-auto mb-4 ${isDragging ? 'text-blue-500' : selectedFile ? 'text-green-500' : 'text-gray-400'}`} />
                    <p className="text-lg font-semibold mb-2">
                      {selectedFile ? selectedFile.name : isDragging ? 'Drop file here' : 'Click to select or drag & drop file'}
                    </p>
                    <p className="text-sm text-gray-500">Only .docx files are allowed</p>
                  </div>
                </label>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex-1 border-t border-gray-300"></div>
                <span className="px-3 py-1 bg-gray-100 text-gray-600 text-sm font-semibold rounded-full">OR</span>
                <div className="flex-1 border-t border-gray-300"></div>
              </div>

              {/* SharePoint Browser */}
              <div>
                <h3 className="text-lg font-semibold text-blue-600 mb-3">Select from SharePoint</h3>
                {selectedSharePointFile ? (
                  <div className="border-2 border-green-500 bg-green-50 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <FileText className="w-12 h-12 text-green-500" />
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{selectedSharePointFile.fileName}</p>
                        <p className="text-sm text-gray-600">{selectedSharePointFile.filePath}</p>
                      </div>
                      <button
                        onClick={() => setSelectedSharePointFile(null)}
                        className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setSharePointBrowserOpenForUpload(true)}
                    className="w-full border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-500 hover:bg-blue-50 transition-all"
                  >
                    <Cloud className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p className="font-semibold text-gray-700">Browse SharePoint for .docx file</p>
                  </button>
                )}
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setUploadDialogOpen(false);
                  setIsDragging(false);
                  setSelectedFile(null);
                  setSelectedSharePointFile(null);
                }}
                className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUploadTrilha}
                disabled={(!selectedFile && !selectedSharePointFile) || loading}
                className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    Upload
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assembly Dialog */}
      {assemblyDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Configure Apostila Assembly</h2>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Apostila Name</label>
                <input
                  type="text"
                  value={assemblyConfig.name}
                  onChange={(e) => setAssemblyConfig({ ...assemblyConfig, name: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Page Limit per Part</label>
                <input
                  type="number"
                  value={assemblyConfig.pageLimit}
                  onChange={(e) => setAssemblyConfig({ ...assemblyConfig, pageLimit: parseInt(e.target.value) })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-sm text-gray-500 mt-1">Maximum pages allowed per part (default: 460)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">SharePoint Folder</label>
                <input
                  type="text"
                  value={assemblyConfig.sharePointDriveName || 'Not selected'}
                  readOnly
                  className={`w-full px-4 py-2.5 border rounded-lg bg-gray-50 ${
                    !assemblyConfig.sharePointDriveId ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {!assemblyConfig.sharePointDriveId && (
                  <p className="text-sm text-red-600 mt-1">Select folder containing OE PDFs</p>
                )}
                <button
                  onClick={() => setSharePointBrowserOpen(true)}
                  className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  <Cloud className="w-5 h-5" />
                  {assemblyConfig.sharePointFolderId ? 'Change Folder Selection' : 'Browse & Select OE Folder'}
                </button>

                {selectedOEFiles.length > 0 && (
                  <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-sm font-semibold text-green-800 mb-2">
                      ✓ Selected {selectedOEFiles.length} OE file(s):
                    </p>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {selectedOEFiles.map((file, index) => (
                        <span key={file.id} className="inline-block px-2 py-1 bg-white border border-green-200 rounded text-xs text-gray-700 m-0.5">
                          {index + 1}. {file.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="addPageNumbers"
                  checked={assemblyConfig.addPageNumbers}
                  onChange={() => setAssemblyConfig({ ...assemblyConfig, addPageNumbers: !assemblyConfig.addPageNumbers })}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
                <label htmlFor="addPageNumbers" className="text-sm font-medium text-gray-700">Add page numbers</label>
              </div>

              {/* NBA-RQ-3: mutually exclusive summary options - whichever is
                  selected always goes at the END of the file (after slides) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Summary (always at the end of the file)</label>
                <div className="space-y-2">
                  {([
                    { value: 'none', label: 'No summary' },
                    { value: 'oa', label: 'OA Summary (one resumo per OA)' },
                    { value: 'full', label: 'Full Summary (single "Resumo {track}.pdf")' },
                  ] as const).map(option => (
                    <div key={option.value} className="flex items-center gap-3">
                      <input
                        type="radio"
                        id={`summaryType-${option.value}`}
                        name="summaryType"
                        checked={assemblyConfig.summaryType === option.value}
                        onChange={() => setAssemblyConfig({ ...assemblyConfig, summaryType: option.value })}
                        className="w-5 h-5 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500"
                      />
                      <label htmlFor={`summaryType-${option.value}`} className="text-sm font-medium text-gray-700">
                        {option.label}
                      </label>
                    </div>
                  ))}
                </div>
                {assemblyConfig.summaryType === 'full' && (
                  <p className="text-xs text-gray-500 mt-1">
                    Fetched from "000-Resumos Certificações Nacionais" by track name (e.g. "Resumo CFG.pdf").
                    If no matching file is found, the apostila is generated without it and a warning is shown.
                  </p>
                )}
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setAssemblyDialogOpen(false)}
                className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateApostila}
                disabled={loading || !assemblyConfig.sharePointDriveId}
                className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Starting...
                  </>
                ) : (
                  <>
                    <Play className="w-5 h-5" />
                    Start Assembly
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NBA-RQ-3: Add Slides Dialog - pick a deck per OA + one layout */}
      {slidesDialogOpen && slidesApostila && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Add Slides</h2>
              <p className="text-sm text-gray-600 mt-1">
                {slidesApostila.name} — pick a slide deck (.pptx) for each OA that should get slides.
                Slides are inserted before the summary; the original apostila stays available.
              </p>
            </div>

            <div className="p-6 space-y-5">
              {/* Layout: single selection per generation (client decision - 1/2/4 only) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Slides per page (single layout)</label>
                <div className="flex gap-3">
                  {([1, 2, 4] as const).map(n => (
                    <button
                      key={n}
                      onClick={() => setSlidesPerPage(n)}
                      className={`flex-1 px-4 py-2.5 rounded-lg border font-semibold transition-colors ${
                        slidesPerPage === n
                          ? 'border-purple-600 bg-purple-50 text-purple-700'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {n} / page
                    </button>
                  ))}
                </div>
              </div>

              {/* Client 2026-09-25: single-step bulk selection (primary action) */}
              <div>
                <button
                  onClick={() => setBulkDeckBrowserOpen(true)}
                  disabled={slidesLoadingOAs || slidesTrilhaOAs.length === 0}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
                >
                  <Cloud className="w-5 h-5" />
                  Select decks from SharePoint (bulk)
                </button>
                <p className="text-xs text-gray-500 mt-1">
                  Open the slides folder once and select all decks together — they're automatically
                  assigned to the trilha's OAs by the number in the filename (e.g. "077_Slides_….pptx" → OA 077).
                </p>
              </div>

              {/* Client 2026-10: ONE ordered list - this order IS the final order
                  in the document. Reorder with ↑↓; decks without an OA are
                  included as extra material (e.g. exam tips). */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Selected decks — final order ({deckList.length})
                  </label>
                  {deckList.length > 0 && (
                    <button
                      onClick={() => setDeckList([])}
                      className="text-xs text-red-600 hover:text-red-700 font-medium"
                    >
                      Clear all
                    </button>
                  )}
                </div>

                {deckList.length === 0 ? (
                  <p className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-4">
                    No decks selected yet — use the bulk button above (or the per-OA list below).
                  </p>
                ) : (
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-64 overflow-y-auto">
                    {deckList.map((deck, idx) => (
                      <div key={deck.fileId} className={`flex items-center gap-2 px-3 py-2 ${!deck.oeCode ? 'bg-yellow-50' : ''}`}>
                        <span className="text-xs text-gray-400 w-5 text-right">{idx + 1}.</span>
                        <div className="flex flex-col">
                          <button
                            onClick={() => moveDeck(idx, -1)}
                            disabled={idx === 0}
                            className="text-xs text-gray-500 hover:text-gray-800 disabled:opacity-25 leading-none"
                            title="Move up"
                          >▲</button>
                          <button
                            onClick={() => moveDeck(idx, 1)}
                            disabled={idx === deckList.length - 1}
                            className="text-xs text-gray-500 hover:text-gray-800 disabled:opacity-25 leading-none"
                            title="Move down"
                          >▼</button>
                        </div>
                        <p className="flex-1 text-xs text-gray-800 truncate" title={deck.fileName}>📎 {deck.fileName}</p>
                        <select
                          value={deck.oeCode || ''}
                          onChange={(e) => setDeckOA(deck.fileId, e.target.value || undefined)}
                          className={`text-xs border rounded px-1.5 py-1 ${deck.oeCode ? 'border-gray-300 text-gray-700' : 'border-yellow-400 text-yellow-800'}`}
                          title="OA this deck belongs to (optional)"
                        >
                          <option value="">— extra (no OA) —</option>
                          {slidesTrilhaOAs.map(oa => (
                            <option key={oa.oaCode} value={oa.oaCode}>OA {oa.oaCode}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => setDeckList(prev => prev.filter(d => d.fileId !== deck.fileId))}
                          className="text-xs text-red-500 hover:text-red-700"
                          title="Remove"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  The order above is exactly the order the decks appear in the final file. Decks
                  marked "extra (no OA)" are included too (grouped under "Material Adicional" in the
                  TOC). If a deck fails to convert it is skipped and flagged in the completion report.
                </p>
              </div>

              {/* Per-OA fallback picker */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  OAs in this trilha (fallback: pick decks for a specific OA)
                </label>
                {slidesLoadingOAs ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-purple-600 animate-spin" />
                  </div>
                ) : slidesTrilhaOAs.length === 0 ? (
                  <p className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-4">
                    No OAs found for this apostila's trilha.
                  </p>
                ) : (
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto">
                    {slidesTrilhaOAs.map(oa => {
                      const count = deckList.filter(d => d.oeCode === oa.oaCode).length;
                      return (
                        <div key={oa.oaCode} className="flex items-center gap-3 px-4 py-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900">
                              OA {oa.oaCode}
                              {count > 0 && (
                                <span className="ml-2 text-xs font-normal text-purple-700">({count} deck{count > 1 ? 's' : ''})</span>
                              )}
                            </p>
                            {oa.title && <p className="text-xs text-gray-500 truncate">{oa.title}</p>}
                          </div>
                          <button
                            onClick={() => setDeckBrowserForOA(oa.oaCode)}
                            className="px-3 py-1.5 text-xs border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50 font-medium transition-colors whitespace-nowrap"
                          >
                            {count > 0 ? '+ Add more' : 'Select deck(s)'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setSlidesDialogOpen(false);
                  setSlidesApostila(null);
                }}
                className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleStartSlidesMerge}
                disabled={deckList.length === 0}
                className="flex items-center gap-2 px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <MonitorPlay className="w-5 h-5" />
                Generate Final File
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SharePoint Browser for Assembly */}
      <SharePointBrowser
        open={sharePointBrowserOpen}
        onClose={() => setSharePointBrowserOpen(false)}
        onSelect={handleSharePointSelect}
        title="Select Folder Containing OE PDF Files"
        selectionMode="folder"
        fileFilter={['.pdf']}
        excludePatterns={['Resumo', 'Summary']}
        initialPath={{
          siteName: 'FK Partners Portal',
          driveName: 'Conteúdo'
        }}
      />

      {/* NBA-RQ-3: SharePoint Browser for per-OA slide deck picking (fallback) */}
      <SharePointBrowser
        open={deckBrowserForOA !== null}
        onClose={() => setDeckBrowserForOA(null)}
        onSelect={handleDeckSelect}
        title={`Select slide deck(s) (.pptx) for OA ${deckBrowserForOA || ''}`}
        selectionMode="multi-file"
        fileFilter={['.pptx']}
        initialPath={{
          siteName: 'FK Partners Portal',
          driveName: 'Conteúdo'
        }}
      />

      {/* Client 2026-09-25: BULK deck selection - one visit to the slides folder,
          decks auto-assigned to OAs by the filename's number prefix */}
      <SharePointBrowser
        open={bulkDeckBrowserOpen}
        onClose={() => setBulkDeckBrowserOpen(false)}
        onSelect={handleBulkDeckSelect}
        title="Select ALL slide decks (.pptx) — auto-assigned to OAs by filename"
        selectionMode="multi-file"
        fileFilter={['.pptx']}
        initialPath={{
          siteName: 'FK Partners Portal',
          driveName: 'Conteúdo'
        }}
      />

      {/* SharePoint Browser for Upload */}
      <SharePointBrowser
        open={sharePointBrowserOpenForUpload}
        onClose={() => setSharePointBrowserOpenForUpload(false)}
        onSelect={handleSharePointSelectForUpload}
        title="Select Trilha .docx from SharePoint"
        selectionMode="file"
        fileFilter={['.docx']}
        initialPath={{
          siteName: 'FK Partners Portal',
          driveName: 'Conteúdo'
        }}
      />
    </div>
  );
};

export default ContentAssemblyDashboard;
